import type { AdvanvedTableGroupedRows, ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import type { LocalDate } from '@zg-rentals/rental-manager-env';
import { LoadingConstants, numberUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';
import { GuestPayFooter } from './GuestPayFooter';
import { Redirect, useHistory } from 'react-router-dom';
import { manifest } from '../../app/routes';
import { useAppGuestPay } from '../../hooks/useGuestPay';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormField,
  Input,
  Label,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import React from 'react';
import type { AdproductsGuestPayZuoraDto } from '@zg-rentals/ts-adproducts-client';
import {
  AdproductsGuestBillingInvoiceApiHandler_saveGuestPayInvoiceSelectionForGuest,
  AdproductsGuestBillingInvoiceApiHandler_sendGuestPaySupportEmail,
  AdproductsUserBillingInvoiceApiHandler_saveGuestPayInvoiceSelection,
  AdproductsUserBillingInvoiceApiHandler_sendGuestPaySupportEmail,
} from '@zg-rentals/ts-adproducts-client';
import {
  emitGuestPaySelectInvoiceCancelEvent,
  emitGuestPaySelectInvoiceContinueEvent,
  emitGuestPaySelectInvoiceEvent,
  emitGuestPaySelectInvoiceHelpModalOpenEvent,
  emitGuestPaySelectInvoiceSubmitHelpEvent,
} from '../../model/GuestPayClickstreamEvents';

export const dateFormat = (date: LocalDate) => `${date.month}/${date.day}/${date.year}`;

const GROUPING_TYPE = { PRIMARY: 'PRIMARY', SECONDARY: 'SECONDARY' } as const;

export const GuestPaySelectInvoicesPage = () => {
  const [invoiceNumberForHelp, setInvoiceNumberForHelp] = React.useState('');
  const [helpRequestDetails, setHelpRequestDetails] = React.useState('');
  const [submitHelpLoadingState, setSubmitHelpLoadingState] = React.useState<LoadingConstants>(
    LoadingConstants.INITIAL,
  );
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);
  const [showHelpModal, setShowHelpModal] = React.useState(false);

  const history = useHistory();
  const { guestPayState, updateGuestPayState } = useAppGuestPay();
  const { user } = useRentalManagerUser();
  const columnConfig: Array<ColumnConfig<AdproductsGuestPayZuoraDto>> = [
    {
      header: 'Due date',
      width: 4,
      align: 'center',
      renderer: (value) => dateFormat(value.dueDate),
    },
    {
      header: 'Invoice number',
      dataKey: 'invoiceNumber',
      width: 4,
      align: 'left',
      renderer: (value: AdproductsGuestPayZuoraDto) => <Text textStyle="body-bold">{value.invoiceNumber}</Text>,
    },
    {
      header: 'Service period',
      width: 4,
      align: 'left',
      renderer: (value) => `${dateFormat(value.serviceStart)}-${dateFormat(value.serviceEnd)}`,
    },
    {
      header: 'Invoice balance',
      width: 2,
      align: 'left',
      sortValue: (invoice) => invoice.balance,
      renderer: (value: AdproductsGuestPayZuoraDto) => (
        <Text textStyle="body-bold">{numberUtils.asCurrency(value.balance)}</Text>
      ),
    },
  ];

  const sortedInvoices = React.useMemo(() => {
    const primaryInvoice = guestPayState.invoices.find(
      (invoice) => invoice.invoiceNumber === guestPayState.primaryInvoice,
    );
    const otherInvoices = guestPayState.invoices.filter(
      (invoice) => invoice.invoiceNumber !== guestPayState.primaryInvoice,
    );
    if (primaryInvoice) {
      otherInvoices.unshift(primaryInvoice);
    }
    return otherInvoices;
  }, [guestPayState.invoices, guestPayState.primaryInvoice]);

  const selectedIndexes: Set<number> = React.useMemo(() => {
    const newIndexes = new Set<number>();
    guestPayState.invoices.forEach((invoice, i: number) => {
      if (guestPayState.selectedInvoices.has(invoice.invoiceNumber)) {
        newIndexes.add(i);
      }
    });
    return newIndexes;
  }, [guestPayState.invoices, guestPayState.selectedInvoices]);

  const subTotal = React.useMemo(() => {
    const calculatedSubtotal = guestPayState.invoices
      .filter((invoice) => guestPayState.selectedInvoices.has(invoice.invoiceNumber))
      .map((invoice) => invoice.balance)
      .reduce((a, b) => a + b, 0);
    return calculatedSubtotal;
  }, [guestPayState.invoices, guestPayState.selectedInvoices]);

  const mainAccountName = React.useMemo(() => {
    if (guestPayState.guestPayLoadingSessionState === LoadingConstants.INITIAL) {
      return undefined;
    }
    return guestPayState.invoices[0].accountName;
  }, [guestPayState.invoices]);

  const handleSaveSelection = () => {
    const selectedInvoiceIds = guestPayState.invoices
      .filter((invoice) => guestPayState.selectedInvoices.has(invoice.invoiceNumber))
      .map((invoice) => {
        return invoice.invoiceId;
      });
    const params = {
      guestPaySessionId: guestPayState.guestPaySessionKey,
      selectedInvoiceIds: selectedInvoiceIds,
    };
    let api;
    if (user.loggedIn) {
      api = AdproductsUserBillingInvoiceApiHandler_saveGuestPayInvoiceSelection(params);
    } else {
      api = AdproductsGuestBillingInvoiceApiHandler_saveGuestPayInvoiceSelectionForGuest(params);
    }
    api
      .then(() => {
        emitGuestPaySelectInvoiceContinueEvent({ user });
        history.push(manifest.guestPayPaymentDetailsPage);
        return;
      })
      .catch(() => {
        // todo handle error
      });
  };

  if (!guestPayState.invoices.length) {
    return <Redirect to={manifest.guestPayPage} />;
  }

  const handleSubmitHelp = async () => {
    setSubmitHelpLoadingState(LoadingConstants.LOADING);

    try {
      if (user.loggedIn) {
        await AdproductsUserBillingInvoiceApiHandler_sendGuestPaySupportEmail({
          invoiceNumber: invoiceNumberForHelp,
          message: helpRequestDetails,
          guestPaySessionId: guestPayState.guestPaySessionKey || '',
        });
      } else {
        await AdproductsGuestBillingInvoiceApiHandler_sendGuestPaySupportEmail({
          invoiceNumber: invoiceNumberForHelp,
          message: helpRequestDetails,
          guestPaySessionId: guestPayState.guestPaySessionKey || '',
        });
      }
      emitGuestPaySelectInvoiceSubmitHelpEvent({ user });
      setSubmitHelpLoadingState(LoadingConstants.SUCCESS);
      setShowHelpModal(false);
      setInvoiceNumberForHelp('');
      setHelpRequestDetails('');
    } catch (error) {
      setSubmitHelpLoadingState(LoadingConstants.ERROR);
    }
  };

  const HelpModal = () => (
    <Modal
      open={showHelpModal}
      onOpenChange={(open) => setShowHelpModal(open)}
      size="md"
      header="Invoice inquiries"
      body={
        <Box>
          {submitHelpLoadingState === LoadingConstants.ERROR && (
            <Alert tone="critical">Something went wrong, please try again later.</Alert>
          )}
          <Text css={{ marginBottom: 'md' }}>
            Let us know if you see an invoice you believe was already paid by sending a note here or emailing {` `}
            <a href="mailto:ar@zillowgroup.com" style={{ color: 'blue', textDecoration: 'underline' }}>
              ar@zillowgroup.com
            </a>
            . Make sure to include the invoice number you&apos;re contacting us about. We should respond within 24
            business hours.
          </Text>
          <FormField
            css={{ marginTop: 'sm', marginBottom: 'sm' }}
            label={<Label>Invoice number</Label>}
            control={
              <Input
                disabled={submitHelpLoadingState === LoadingConstants.LOADING}
                value={invoiceNumberForHelp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNumberForHelp(e.target.value)}
              />
            }
          />
          <FormField
            css={{ marginBottom: 'sm' }}
            label={<Label>Details of request</Label>}
            control={
              <Input
                disabled={submitHelpLoadingState === LoadingConstants.LOADING}
                value={helpRequestDetails}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHelpRequestDetails(e.target.value)}
              />
            }
          />
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Modal actions">
          <Button tone="neutral" emphasis="outlined" onClick={() => setShowHelpModal(false)}>
            Cancel
          </Button>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmitHelp}
            disabled={submitHelpLoadingState === LoadingConstants.LOADING}
          >
            {submitHelpLoadingState === LoadingConstants.LOADING ? 'Sending...' : 'Send'}
          </Button>
        </ButtonGroup>
      }
    />
  );

  const groupingRenderer = (group: AdvanvedTableGroupedRows<AdproductsGuestPayZuoraDto>) => {
    if (group.groupId === GROUPING_TYPE.PRIMARY) {
      return (
        <Box css={{ marginBlock: 'xs', paddingLeft: 'md' }}>
          <Text textStyle="heading-sm">Selected invoice</Text>
        </Box>
      );
    }
    return (
      <Box css={{ marginBlock: 'xs', paddingLeft: 'md' }}>
        <Paragraph textStyle="body-bold">Other due invoices</Paragraph>
        <Text>
          To avoid account suspension or late fees, prioritize paying invoices before their due date. See an invoice
          that you already paid?{' '}
          <TextButton
            onClick={() => {
              emitGuestPaySelectInvoiceHelpModalOpenEvent({ user });
              setShowHelpModal(true);
            }}
          >
            Contact us
          </TextButton>{' '}
          to resolve it.
        </Text>
      </Box>
    );
  };
  return (
    <PageWrapper title={`Invoices for ${mainAccountName}`} subTitle={`Account number: ${guestPayState.accountNumber}`}>
      <Box css={{ position: 'relative' }}>
        <Box css={{ position: 'absolute', top: '-30px', right: '0px' }}>
          <Paragraph textStyle="body-bold">
            {guestPayState.selectedInvoices.size} invoice{guestPayState.selectedInvoices.size !== 1 ? 's' : ''} selected
          </Paragraph>
        </Box>
        <Modal
          open={isCancelModalOpen}
          onOpenChange={(open) => setIsCancelModalOpen(open)}
          size="sm"
          header="Exit now?"
          body={<Text>You&apos;ll lose your changes and have to start over from the beginning.</Text>}
          footer={
            <ButtonGroup aria-label="Modal actions">
              <Button tone="neutral" emphasis="outlined" onClick={() => setIsCancelModalOpen(false)}>
                Cancel
              </Button>
              <Button
                tone="brand"
                emphasis="filled"
                onClick={() => {
                  emitGuestPaySelectInvoiceCancelEvent({ user });
                  history.push(manifest.guestPayPage);
                }}
              >
                Exit
              </Button>
            </ButtonGroup>
          }
        />
        <AdvancedTable
          rowSelectionDisabled={(row) => !row.balance}
          rowPaddingY={20}
          columnConfig={columnConfig}
          data={sortedInvoices}
          selected={selectedIndexes}
          onSelectRow={(rowSelection) => {
            const newSelectedInvoices = new Set<string>();
            const invoices = sortedInvoices.map((invoice, i) => {
              const newInvoice = { ...invoice, selectedForPayment: false };
              if (rowSelection.has(i)) {
                newSelectedInvoices.add(invoice.invoiceNumber);
                newInvoice.selectedForPayment = true;
              }
              return newInvoice;
            });
            updateGuestPayState({ invoices: invoices, selectedInvoices: newSelectedInvoices });
            emitGuestPaySelectInvoiceEvent({ user });
            return;
          }}
          groupingFunction={(invoice: AdproductsGuestPayZuoraDto) =>
            invoice.invoiceNumber === guestPayState.primaryInvoice ? GROUPING_TYPE.PRIMARY : GROUPING_TYPE.SECONDARY
          }
          groupingHeaderRenderer={groupingRenderer}
          enableGroupSelection={false}
        />
      </Box>
      <GuestPayFooter
        handleBackButton={() => {
          setIsCancelModalOpen(true);
        }}
        handleNext={handleSaveSelection}
        subTotal={subTotal}
        isNextDisabled={selectedIndexes.size === 0}
      />
      <HelpModal />
    </PageWrapper>
  );
};
